import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import cytoscape, { type Core, type ElementDefinition } from "cytoscape";
import { Badge, Button, Card, CardHeader, EmptyState } from "@/components/primitives";
import { useDisplaySettings } from "@/hooks/useDisplaySettings";
import { fmtNumber, truncateMiddle } from "@/lib/format";
import type { UnderAttackHotClusterV1, UnderAttackSeedInputV1 } from "@/types/v1";

type AttackNodeKind = "ip" | "asn" | "ja4" | "path";
type GraphDensity = "focused" | "balanced" | "expanded";
type SelectedEntity = { type: "node"; id: string } | { type: "edge"; id: string } | null;

interface AttackNodeData {
  kind: AttackNodeKind;
  fullLabel: string;
  isSeed: boolean;
  events: number;
  highRisk: number;
  filterPreset: Record<string, string>;
  related: Record<AttackNodeKind, string[]>;
}

interface AttackEdgeData {
  sourceLabel: string;
  targetLabel: string;
  highRisk: number;
  events: number;
}

interface AttackGraphNode {
  id: string;
  label: string;
  size: number;
  fill: string;
  position: { x: number; y: number };
  data: AttackNodeData;
}

interface AttackGraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  size: number;
  data: AttackEdgeData;
}

interface AttackGraphModel {
  nodes: AttackGraphNode[];
  edges: AttackGraphEdge[];
  nodeById: Map<string, AttackGraphNode>;
  edgeById: Map<string, AttackGraphEdge>;
  seedNodeIds: string[];
  hiddenNodeCount: number;
  hiddenEdgeCount: number;
}

interface GraphTheme {
  bg: string;
  surface1: string;
  surface2: string;
  border: string;
  borderStrong: string;
  fg: string;
  fgMuted: string;
  fgSubtle: string;
  accent: string;
  high: string;
  edge: string;
  kindFill: Record<AttackNodeKind, string>;
  kindStroke: Record<AttackNodeKind, string>;
}

const KIND_LABEL: Record<AttackNodeKind, string> = {
  ip: "IP",
  asn: "ASN",
  ja4: "JA4",
  path: "Path",
};

const KIND_ORDER: AttackNodeKind[] = ["ip", "asn", "ja4", "path"];
const LANE_X: Record<AttackNodeKind, number> = { ip: 230, asn: 430, ja4: 650, path: 850 };
const DENSITY_LIMITS: Record<GraphDensity, { clusters: number; nodes: number; edges: number; ipsPerCluster: number; edgeFloor: number }> = {
  focused: { clusters: 7, nodes: 24, edges: 34, ipsPerCluster: 2, edgeFloor: 0.34 },
  balanced: { clusters: 10, nodes: 34, edges: 48, ipsPerCluster: 3, edgeFloor: 0.18 },
  expanded: { clusters: 14, nodes: 48, edges: 72, ipsPerCluster: 4, edgeFloor: 0 },
};

function graphThemeFromCss(): GraphTheme {
  if (typeof window === "undefined") return fallbackGraphTheme();
  const styles = getComputedStyle(document.documentElement);
  const color = (name: string, fallback: string) => rgbToken(styles.getPropertyValue(name), fallback);
  const theme: GraphTheme = {
    bg: color("--bg", "#090b0f"),
    surface1: color("--surface-1", "#0e1117"),
    surface2: color("--surface-2", "#14181f"),
    border: color("--border", "#262c36"),
    borderStrong: color("--border-strong", "#38404c"),
    fg: color("--fg", "#e6e8ed"),
    fgMuted: color("--fg-muted", "#9ea5b2"),
    fgSubtle: color("--fg-subtle", "#6e7684"),
    accent: color("--accent", "#6398de"),
    high: color("--sev-high", "#dc4e54"),
    edge: color("--fg-subtle", "#6e7684"),
    kindFill: {
      ip: "#2563eb",
      asn: "#7c3aed",
      ja4: "#db2777",
      path: "#059669",
    },
    kindStroke: {
      ip: "#93c5fd",
      asn: "#c4b5fd",
      ja4: "#f9a8d4",
      path: "#6ee7b7",
    },
  };
  return theme;
}

function rgbToken(value: string, fallback: string): string {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  if (trimmed.startsWith("#") || trimmed.startsWith("rgb")) return trimmed;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  return parts.length === 3 ? `rgb(${parts.join(", ")})` : fallback;
}

function alphaColor(color: string, alpha: number): string {
  const rgb = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const value = hex[1].length === 3
      ? hex[1].split("").map((char) => char + char).join("")
      : hex[1];
    const red = parseInt(value.slice(0, 2), 16);
    const green = parseInt(value.slice(2, 4), 16);
    const blue = parseInt(value.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }
  return color;
}

function fallbackGraphTheme(): GraphTheme {
  return {
    bg: "#090b0f",
    surface1: "#0e1117",
    surface2: "#14181f",
    border: "#262c36",
    borderStrong: "#38404c",
    fg: "#e6e8ed",
    fgMuted: "#9ea5b2",
    fgSubtle: "#6e7684",
    accent: "#6398de",
    high: "#dc4e54",
    edge: "#6e7684",
    kindFill: { ip: "#2563eb", asn: "#7c3aed", ja4: "#db2777", path: "#059669" },
    kindStroke: { ip: "#93c5fd", asn: "#c4b5fd", ja4: "#f9a8d4", path: "#6ee7b7" },
  };
}

export function AttackRelationshipGraph({ clusters, seeds }: { clusters: UnderAttackHotClusterV1[]; seeds: UnderAttackSeedInputV1 }) {
  const display = useDisplaySettings();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cytoscapeRef = useRef<Core | null>(null);
  const selectedRef = useRef<SelectedEntity>(null);
  const initialFitKeyRef = useRef<string>("");
  const [selected, setSelected] = useState<SelectedEntity>(null);
  const [showLabels, setShowLabels] = useState(true);
  const [showLowWeightEdges, setShowLowWeightEdges] = useState(true);
  const [density, setDensity] = useState<GraphDensity>("balanced");
  const [layoutKey, setLayoutKey] = useState(0);

  const theme = useMemo(() => graphThemeFromCss(), [display.resolvedAppearance, display.palette]);
  const graph = useMemo(() => buildAttackGraph(clusters, seeds, theme, density), [clusters, seeds, theme, density]);
  const maxNodeHighRisk = useMemo(() => Math.max(1, ...graph.nodes.map((node) => node.data.highRisk)), [graph.nodes]);
  const visibleEdges = useMemo(() => {
    if (showLowWeightEdges) return graph.edges;
    const maxHighRisk = Math.max(1, ...graph.edges.map((edge) => edge.data.highRisk));
    return graph.edges.filter((edge) => edge.data.highRisk / maxHighRisk >= 0.22);
  }, [graph.edges, showLowWeightEdges]);

  const selectedNode = selected?.type === "node" ? graph.nodeById.get(selected.id) : null;
  const selectedEdge = selected?.type === "edge" ? graph.edgeById.get(selected.id) : null;
  const selectedLabel = selectedNode?.data.fullLabel ?? selectedEdge?.label ?? "None";

  useEffect(() => {
    if (!containerRef.current || !graph.nodes.length) return;
    setSelected((current) => {
      if (!current) return null;
      if (current.type === "node" && graph.nodeById.has(current.id)) return current;
      if (current.type === "edge" && graph.edgeById.has(current.id)) return current;
      return null;
    });

    const cy = cytoscape({
      container: containerRef.current,
      elements: toCytoscapeElements(graph.nodes, visibleEdges, maxNodeHighRisk),
      minZoom: 0.22,
      maxZoom: 3,
      userZoomingEnabled: false,
      autoungrabify: false,
      boxSelectionEnabled: true,
      style: graphStyles(theme, showLabels),
      layout: { name: "preset", fit: false },
    });

    cytoscapeRef.current = cy;
    const container = containerRef.current;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = container?.getBoundingClientRect();
      const renderedPosition = rect
        ? { x: event.clientX - rect.left, y: event.clientY - rect.top }
        : undefined;
      zoomGraph(cy, event.deltaY < 0 ? 1.28 : 1 / 1.28, renderedPosition);
    };
    container?.addEventListener("wheel", onWheel, { passive: false });
    cy.on("tap", "node", (event) => setSelected({ type: "node", id: event.target.id() }));
    cy.on("tap", "edge", (event) => setSelected({ type: "edge", id: event.target.id() }));
    cy.on("tap", (event) => {
      if (event.target === cy) setSelected(null);
    });
    cy.on("mouseover", "node, edge", (event) => highlightNeighborhood(cy, event.target.id(), "hover"));
    cy.on("mouseout", "node, edge", () => highlightNeighborhood(cy, selectedRef.current?.id ?? null));
    const initialFitKey = `${graph.nodes.map((node) => node.id).join("|")}::${density}::${layoutKey}`;
    if (initialFitKeyRef.current !== initialFitKey) {
      initialFitKeyRef.current = initialFitKey;
      window.requestAnimationFrame(() => fitGraph(cy, 72));
    }

    return () => {
      container?.removeEventListener("wheel", onWheel);
      cy.stop();
      cy.destroy();
      cytoscapeRef.current = null;
    };
  }, [graph.nodes, graph.nodeById, graph.edgeById, visibleEdges, maxNodeHighRisk, density, layoutKey, theme]);

  useEffect(() => {
    const cy = cytoscapeRef.current;
    if (!cy) return;
    cy.style(graphStyles(theme, showLabels)).update();
  }, [showLabels, theme]);

  useEffect(() => {
    selectedRef.current = selected;
    highlightNeighborhood(cytoscapeRef.current, selected?.id ?? null);
  }, [selected]);

  const focusSelection = () => {
    const cy = cytoscapeRef.current;
    if (!cy || !selected) return;
    const element = cy.getElementById(selected.id);
    if (!element.length) return;
    cy.animate({ center: { eles: element }, zoom: Math.max(cy.zoom(), 1.05) }, { duration: 220 });
  };

  if (!clusters.length) return <Card><EmptyState title="No hot relationships found." /></Card>;

  return (
    <Card className="overflow-hidden" padded={false}>
      <div className="border-b border-border p-4">
        <CardHeader
          title="Attack relationship map"
          subtitle="A stable incident map of entered indicators and the strongest IP, ASN, JA4, and path relationships."
          action={<Badge tone="high">Interactive</Badge>}
        />
        <div className="mb-3 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-5">
          <SummaryTile label="Visible nodes" value={fmtNumber(graph.nodes.length)} muted={graph.hiddenNodeCount ? `${graph.hiddenNodeCount} hidden by density` : undefined} />
          <SummaryTile label="Visible edges" value={fmtNumber(visibleEdges.length)} muted={graph.hiddenEdgeCount ? `${graph.hiddenEdgeCount} hidden by density` : undefined} />
          <SummaryTile label="Entered indicators" value={fmtNumber(graph.seedNodeIds.length)} muted={graph.seedNodeIds.length ? "Marked with red rings" : "Related-only view"} />
          <SummaryTile label="Density" value={densityLabel(density)} muted="Topology plus risk score" />
          <SummaryTile label="Selected" value={truncateMiddle(selectedLabel, 26)} muted={selected ? "Inspector active" : "Click a node or edge"} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="default" title="Zoom in" onClick={() => zoomGraph(cytoscapeRef.current, 1.35)}>+</Button>
          <Button size="sm" variant="default" title="Zoom out" onClick={() => zoomGraph(cytoscapeRef.current, 1 / 1.35)}>-</Button>
          <Button size="sm" variant="default" title="Fit graph to view" onClick={() => fitGraph(cytoscapeRef.current, 64)}>Fit</Button>
          <Button size="sm" variant="ghost" title="Focus the selected node or edge" disabled={!selected} onClick={focusSelection}>Focus selection</Button>
          <Button size="sm" variant="ghost" title="Reset camera" onClick={() => fitGraph(cytoscapeRef.current, 64)}>Reset view</Button>
          <Button size="sm" variant={showLabels ? "primary" : "ghost"} title="Toggle node labels" onClick={() => setShowLabels((value) => !value)}>Labels</Button>
          <Button size="sm" variant={showLowWeightEdges ? "primary" : "ghost"} title="Show or hide lower-weight relationships" onClick={() => setShowLowWeightEdges((value) => !value)}>Low-weight edges</Button>
          <div className="inline-flex rounded-md border border-border bg-surface-2 p-0.5" aria-label="Graph density">
            {(["focused", "balanced", "expanded"] as GraphDensity[]).map((value) => (
              <button
                key={value}
                type="button"
                className={clsx("rounded px-2 py-1 text-xs capitalize transition", density === value ? "bg-accent text-white" : "text-fg-muted hover:text-fg")}
                onClick={() => setDensity(value)}
              >
                {value}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" title="Restore the semantic lane layout" onClick={() => { setSelected(null); setLayoutKey((value) => value + 1); }}>Reset layout</Button>
          <div className="ml-auto flex flex-wrap gap-1.5 text-[11px] text-fg-subtle">
            {KIND_ORDER.map((kind) => (
              <span key={kind} className="inline-flex items-center gap-1 rounded border border-border bg-surface-2 px-1.5 py-0.5">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: theme.kindFill[kind] }} />{KIND_LABEL[kind]}
              </span>
            ))}
            <span className="inline-flex items-center gap-1 rounded border border-sev-high/40 bg-sev-high/10 px-1.5 py-0.5 text-sev-high">
              <span className="h-2 w-2 rounded-full border-2 border-sev-high bg-transparent" />Entered
            </span>
          </div>
        </div>
      </div>

      <div className="grid min-h-[680px] gap-0 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div
          className="relative min-h-[560px] overflow-hidden sm:min-h-[640px]"
          style={{
            background: `linear-gradient(90deg, ${alphaColor(theme.kindFill.ip, 0.075)} 0 25%, ${alphaColor(theme.kindFill.asn, 0.065)} 25% 50%, ${alphaColor(theme.kindFill.ja4, 0.06)} 50% 75%, ${alphaColor(theme.kindFill.path, 0.07)} 75% 100%), linear-gradient(180deg, ${theme.surface1}, ${theme.bg})`,
          }}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 grid grid-cols-4 border-b border-border/80 bg-surface-1/88 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle shadow-card backdrop-blur">
            {KIND_ORDER.map((kind) => <div key={kind} className="text-center">{KIND_LABEL[kind]}</div>)}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-1/4 border-l border-border/60" />
          <div className="pointer-events-none absolute inset-y-0 left-1/2 border-l border-border/60" />
          <div className="pointer-events-none absolute inset-y-0 left-3/4 border-l border-border/60" />
          <div ref={containerRef} className="absolute inset-0" aria-label="Under Attack Mode interactive relationship graph" />
          <div className="pointer-events-none absolute bottom-3 left-3 rounded-md border border-border bg-surface-1/90 px-2 py-1 text-[11px] text-fg-muted shadow-card">
            Scroll to zoom. Drag nodes to make space. The camera moves only when you ask it to.
          </div>
        </div>
        <GraphDetails node={selectedNode} edge={selectedEdge} theme={theme} />
      </div>
    </Card>
  );
}

function SummaryTile({ label, value, muted }: { label: string; value: string; muted?: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-2 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-fg" title={value}>{value}</div>
      {muted ? <div className="mt-0.5 truncate text-[11px] text-fg-subtle" title={muted}>{muted}</div> : null}
    </div>
  );
}

function densityLabel(density: GraphDensity): string {
  if (density === "focused") return "Focused";
  if (density === "expanded") return "Expanded";
  return "Balanced";
}

function GraphDetails({ node, edge, theme }: { node: AttackGraphNode | null | undefined; edge: AttackGraphEdge | null | undefined; theme: GraphTheme }) {
  if (!node && !edge) {
    return (
      <aside className="border-t border-border bg-surface-1 p-4 xl:border-l xl:border-t-0">
        <div className="text-sm font-semibold text-fg">Inspector</div>
        <p className="mt-2 text-xs text-fg-muted">Select any entity or relationship to inspect counts, related indicators, and pivot filters.</p>
      </aside>
    );
  }

  if (edge) {
    const data = edge.data;
    return (
      <aside className="border-t border-border bg-surface-1 p-4 xl:border-l xl:border-t-0">
        <div className="text-xs uppercase tracking-wide text-fg-subtle">Relationship</div>
        <div className="mt-1 break-words text-sm font-semibold text-fg">{data.sourceLabel} {"->"} {data.targetLabel}</div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <DetailMetric label="Events" value={fmtNumber(data.events, { compact: true })} />
          <DetailMetric label="High-risk" value={fmtNumber(data.highRisk, { compact: true })} danger />
        </div>
      </aside>
    );
  }

  if (!node) return null;
  const data = node.data;
  return (
    <aside className="border-t border-border bg-surface-1 p-4 xl:border-l xl:border-t-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-xs font-semibold text-fg">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: theme.kindFill[data.kind] }} />
            {KIND_LABEL[data.kind]}
          </span>
          {data.isSeed ? <Badge tone="high">Entered</Badge> : null}
        </div>
        <Button size="sm" variant="ghost" onClick={() => pivotToInvestigations(data.filterPreset)}>Open</Button>
      </div>
      <div className="mt-3 break-words font-mono text-sm font-semibold text-fg">{data.fullLabel}</div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <DetailMetric label="Events" value={fmtNumber(data.events, { compact: true })} />
        <DetailMetric label="High-risk" value={fmtNumber(data.highRisk, { compact: true })} danger />
      </div>
      <div className="mt-4 space-y-3">
        {(Object.keys(KIND_LABEL) as AttackNodeKind[]).filter((kind) => kind !== data.kind).map((kind) => (
          <RelatedList key={kind} title={`Related ${KIND_LABEL[kind]}`} values={data.related[kind]} />
        ))}
      </div>
    </aside>
  );
}

function DetailMetric({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-surface-2 p-2">
      <div className="text-[11px] uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className={clsx("mt-1 font-semibold tabular", danger ? "text-sev-high" : "text-fg")}>{value}</div>
    </div>
  );
}

function RelatedList({ title, values }: { title: string; values: string[] }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-fg-subtle">{title}</div>
      {values.length ? (
        <div className="space-y-1">
          {values.slice(0, 6).map((value) => (
            <div key={value} className="truncate rounded border border-border bg-surface-2 px-2 py-1 font-mono text-xs text-fg" title={value}>{value}</div>
          ))}
        </div>
      ) : <div className="text-xs text-fg-subtle">None observed</div>}
    </div>
  );
}

function toCytoscapeElements(nodes: AttackGraphNode[], edges: AttackGraphEdge[], maxNodeHighRisk: number): ElementDefinition[] {
  return [
    ...nodes.map((node) => ({
      group: "nodes" as const,
      classes: clsx(`kind-${node.data.kind}`, node.data.isSeed && "seed-node"),
      data: {
        id: node.id,
        label: node.label,
        kind: node.data.kind,
        isSeed: node.data.isSeed,
        color: node.fill,
        stroke: node.data.isSeed ? "#dc2626" : node.fill,
        highRisk: node.data.highRisk,
        events: node.data.events,
        size: (node.data.isSeed ? 36 : 28) + Math.sqrt(node.data.highRisk / maxNodeHighRisk) * (node.data.isSeed ? 28 : 24),
      },
      position: node.position,
    })),
    ...edges.map((edge) => ({
      group: "edges" as const,
      data: {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        highRisk: edge.data.highRisk,
        events: edge.data.events,
        size: edge.size,
      },
    })),
  ];
}

function graphStyles(theme: GraphTheme, showLabels: boolean): cytoscape.StylesheetJson {
  return [
    {
      selector: "node",
      style: {
        width: "data(size)",
        height: "data(size)",
        "background-color": "data(color)",
        "border-color": "data(stroke)",
        "border-width": 2,
        "underlay-color": "transparent",
        "underlay-opacity": 0,
        "underlay-padding": 0,
        label: showLabels ? "data(label)" : "",
        color: theme.fg,
        "font-family": "Inter, system-ui, sans-serif",
        "font-size": 10,
        "font-weight": 650,
        "text-valign": "bottom",
        "text-halign": "center",
        "text-margin-y": 10,
        "text-max-width": "128px",
        "text-wrap": "ellipsis",
        "text-background-color": theme.surface1,
        "text-background-opacity": 0.92,
        "text-background-padding": "2px",
        "text-background-shape": "roundrectangle",
        "text-outline-color": theme.bg,
        "text-outline-width": 1,
      },
    },
    { selector: ".kind-ip", style: { shape: "ellipse" } },
    { selector: ".kind-asn", style: { shape: "round-rectangle" } },
    { selector: ".kind-ja4", style: { shape: "diamond" } },
    { selector: ".kind-path", style: { shape: "hexagon" } },
    {
      selector: ".seed-node",
      style: {
        "border-color": theme.high,
        "border-width": 4,
        "underlay-color": theme.high,
        "underlay-opacity": 0.12,
        "underlay-padding": 8,
        "z-index": 30,
      },
    },
    {
      selector: "edge",
      style: {
        width: "data(size)",
        "line-color": theme.edge,
        "target-arrow-color": theme.edge,
        opacity: 0.62,
        "curve-style": "straight",
        "target-arrow-shape": "triangle",
        "arrow-scale": 0.72,
        label: "",
      },
    },
    {
      selector: ".selected-entity",
      style: {
        "border-color": theme.high,
        "border-width": 5,
        "line-color": theme.high,
        "target-arrow-color": theme.high,
        opacity: 1,
        "z-index": 50,
      },
    },
    {
      selector: ".active-neighbor",
      style: {
        opacity: 1,
        "line-color": theme.accent,
        "target-arrow-color": theme.accent,
        "border-color": theme.accent,
        "z-index": 30,
      },
    },
    {
      selector: ".hover-preview",
      style: {
        "underlay-color": theme.accent,
        "underlay-opacity": 0.12,
        "underlay-padding": 8,
      },
    },
    {
      selector: ".dimmed",
      style: { opacity: 0.42 },
    },
  ];
}

function zoomGraph(cy: Core | null, factor: number, renderedPosition?: { x: number; y: number }) {
  if (!cy) return;
  const extent = cy.extent();
  cy.zoom({
    level: Math.max(cy.minZoom(), Math.min(cy.maxZoom(), cy.zoom() * factor)),
    renderedPosition: renderedPosition ?? { x: (extent.x1 + extent.x2) / 2, y: (extent.y1 + extent.y2) / 2 },
  });
}

function fitGraph(cy: Core | null, padding: number) {
  if (!cy) return;
  const elements = cy.elements();
  if (!elements.length) return;
  cy.fit(elements, padding);
  cy.center(elements);
}

function highlightNeighborhood(cy: Core | null, focusId: string | null, mode: "selected" | "hover" = "selected") {
  if (!cy) return;
  cy.elements().removeClass("selected-entity active-neighbor hover-preview dimmed");
  if (!focusId) return;
  const focus = cy.getElementById(focusId);
  if (!focus.length) return;
  const neighborhood = focus.isNode() ? focus.closedNeighborhood() : (focus as cytoscape.EdgeSingular).connectedNodes().union(focus);
  cy.elements().difference(neighborhood).addClass("dimmed");
  neighborhood.addClass("active-neighbor");
  if (mode === "hover") neighborhood.addClass("hover-preview");
  focus.addClass("selected-entity");
}

function buildAttackGraph(clusters: UnderAttackHotClusterV1[], seeds: UnderAttackSeedInputV1, theme: GraphTheme, density: GraphDensity): AttackGraphModel {
  const limits = DENSITY_LIMITS[density];
  const nodeStats = new Map<string, AttackGraphNode>();
  const edgeStats = new Map<string, AttackGraphEdge>();
  const seedIds = seedNodeIds(seeds);

  const addNode = (kind: AttackNodeKind, value: string, cluster: UnderAttackHotClusterV1, split = 1) => {
    const id = `${kind}:${value}`;
    const isSeed = isSeedNode(kind, value, seedIds, seeds);
    const existing = nodeStats.get(id);
    const events = cluster.events / split;
    const highRisk = cluster.highRisk / split;
    if (existing) {
      existing.data.events += events;
      existing.data.highRisk += highRisk;
      existing.size = existing.data.highRisk;
      addRelated(existing.data, cluster);
      return id;
    }

    const data: AttackNodeData = {
      kind,
      fullLabel: value,
      isSeed,
      events,
      highRisk,
      filterPreset: filterPresetFor(kind, value, cluster),
      related: { ip: [], asn: [], ja4: [], path: [] },
    };
    addRelated(data, cluster);
    nodeStats.set(id, {
      id,
      label: labelFor(kind, value),
      size: highRisk,
      fill: theme.kindFill[kind],
      position: { x: LANE_X[kind], y: 120 },
      data,
    });
    return id;
  };

  const addEdge = (source: string, target: string, cluster: UnderAttackHotClusterV1, split = 1) => {
    const id = `${source}->${target}`;
    const sourceNode = nodeStats.get(source);
    const targetNode = nodeStats.get(target);
    if (!sourceNode || !targetNode) return;
    const events = cluster.events / split;
    const highRisk = cluster.highRisk / split;
    const existing = edgeStats.get(id);
    if (existing) {
      existing.data.events += events;
      existing.data.highRisk += highRisk;
      existing.size = sizeForEdge(existing.data.highRisk);
      return;
    }
    edgeStats.set(id, {
      id,
      source,
      target,
      label: `${sourceNode.label} -> ${targetNode.label}`,
      size: sizeForEdge(highRisk),
      data: {
        sourceLabel: sourceNode.data.fullLabel,
        targetLabel: targetNode.data.fullLabel,
        events,
        highRisk,
      },
    });
  };

  for (const cluster of clusters.slice(0, limits.clusters)) {
    if (!cluster.path) continue;
    const pathId = addNode("path", cluster.path, cluster);
    const ja4Id = cluster.ja4 ? addNode("ja4", cluster.ja4, cluster) : null;
    const asnId = cluster.asn ? addNode("asn", `AS${cluster.asn}`, cluster) : null;
    const ips = cluster.topIps.slice(0, limits.ipsPerCluster);

    if (ja4Id) addEdge(ja4Id, pathId, cluster);
    if (asnId && ja4Id) addEdge(asnId, ja4Id, cluster);
    for (const ip of ips) {
      const ipId = addNode("ip", ip, cluster, Math.max(1, ips.length));
      if (asnId) addEdge(ipId, asnId, cluster, Math.max(1, ips.length));
      else if (ja4Id) addEdge(ipId, ja4Id, cluster, Math.max(1, ips.length));
    }
  }

  const rankedNodes = [...nodeStats.values()]
    .sort((a, b) => Number(b.data.isSeed) - Number(a.data.isSeed) || b.data.highRisk - a.data.highRisk || a.data.fullLabel.localeCompare(b.data.fullLabel));
  const candidateNodes = rankedNodes.slice(0, limits.nodes)
    .map((node) => ({
      ...node,
      data: {
        ...node.data,
        events: Math.round(node.data.events),
        highRisk: Math.round(node.data.highRisk),
        related: mapRelated(node.data.related),
      },
      size: Math.max(1, Math.round(node.data.highRisk)),
    }));
  const visibleNodeIds = new Set(candidateNodes.map((node) => node.id));
  const allVisibleEdges = [...edgeStats.values()]
    .filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target))
    .sort((a, b) => b.data.highRisk - a.data.highRisk || a.id.localeCompare(b.id));
  const maxEdgeHighRisk = Math.max(1, ...allVisibleEdges.map((edge) => edge.data.highRisk));
  const edges = allVisibleEdges
    .filter((edge) => edge.data.highRisk / maxEdgeHighRisk >= limits.edgeFloor)
    .slice(0, limits.edges)
    .map((edge) => ({
      ...edge,
      data: { ...edge.data, events: Math.round(edge.data.events), highRisk: Math.round(edge.data.highRisk) },
      size: sizeForEdge(edge.data.highRisk),
    }));
  const connectedNodeIds = new Set<string>();
  for (const edge of edges) {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  }
  const nodes = assignLanePositions(candidateNodes.filter((node) => node.data.isSeed || connectedNodeIds.has(node.id)));

  const visibleSeedNodeIds = nodes.filter((node) => node.data.isSeed).map((node) => node.id);

  return {
    nodes,
    edges,
    nodeById: new Map(nodes.map((node) => [node.id, node])),
    edgeById: new Map(edges.map((edge) => [edge.id, edge])),
    seedNodeIds: visibleSeedNodeIds,
    hiddenNodeCount: Math.max(0, rankedNodes.length - nodes.length),
    hiddenEdgeCount: Math.max(0, allVisibleEdges.length - edges.length),
  };
}

function assignLanePositions(nodes: AttackGraphNode[]): AttackGraphNode[] {
  const laneCounts: Record<AttackNodeKind, number> = { ip: 0, asn: 0, ja4: 0, path: 0 };
  return nodes
    .sort((a, b) => KIND_ORDER.indexOf(a.data.kind) - KIND_ORDER.indexOf(b.data.kind) || Number(b.data.isSeed) - Number(a.data.isSeed) || b.data.highRisk - a.data.highRisk || a.data.fullLabel.localeCompare(b.data.fullLabel))
    .map((node) => {
      const index = laneCounts[node.data.kind]++;
      const sideOffset = index % 2 === 0 ? -12 : 12;
      return {
        ...node,
        position: {
          x: LANE_X[node.data.kind] + sideOffset,
          y: 210 + index * 92 + (node.data.isSeed ? 0 : 18),
        },
      };
    });
}

function seedNodeIds(seeds: UnderAttackSeedInputV1): Set<string> {
  return new Set([
    ...seeds.ips.map((value) => `ip:${value}`),
    ...seeds.asns.map((value) => `asn:AS${value.replace(/^AS/i, "")}`),
    ...seeds.ja4s.map((value) => `ja4:${value}`),
    ...seeds.paths.map((value) => `path:${normalizePath(value)}`),
  ]);
}

function isSeedNode(kind: AttackNodeKind, value: string, seedIds: Set<string>, seeds: UnderAttackSeedInputV1): boolean {
  const id = `${kind}:${value}`;
  if (seedIds.has(id)) return true;
  if (kind !== "path") return false;
  return seeds.paths.some((seedPath) => value.startsWith(normalizePath(seedPath)));
}

function normalizePath(path: string): string {
  const trimmed = path.trim();
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function addRelated(data: AttackNodeData, cluster: UnderAttackHotClusterV1) {
  if (cluster.asn) data.related.asn.push(`AS${cluster.asn}`);
  if (cluster.ja4) data.related.ja4.push(cluster.ja4);
  if (cluster.path) data.related.path.push(cluster.path);
  data.related.ip.push(...cluster.topIps.slice(0, 3));
}

function mapRelated(related: Record<AttackNodeKind, string[]>): Record<AttackNodeKind, string[]> {
  return {
    ip: unique(related.ip),
    asn: unique(related.asn),
    ja4: unique(related.ja4),
    path: unique(related.path),
  };
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function labelFor(kind: AttackNodeKind, value: string): string {
  if (kind === "ja4") return truncateMiddle(value, 22);
  if (kind === "path") return truncateMiddle(value, 30);
  return value;
}

function filterPresetFor(kind: AttackNodeKind, value: string, cluster: UnderAttackHotClusterV1): Record<string, string> {
  const base: Record<string, string> = {};
  if (cluster.endpointGroup) base.endpointGroups = cluster.endpointGroup;
  if (cluster.method) base.method = cluster.method;
  if (kind === "ip") base.ip = value;
  if (kind === "asn") base.asn = value.replace(/^AS/i, "");
  if (kind === "ja4") base.ja4 = value;
  if (kind === "path") base.pathPrefix = value;
  return base;
}

function sizeForEdge(highRisk: number): number {
  return Math.max(1.1, Math.min(4.6, Math.sqrt(Math.max(1, highRisk)) / 10));
}

function pivotToInvestigations(filters: Record<string, string>) {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", "investigations");
  for (const [key, value] of Object.entries(filters)) url.searchParams.set(key, value);
  window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
